import {
  TableOrderItemSchema,
  type TableOrderItem,
  type TableOrderBillData,
} from "../types/table-order";

// The `tostore` JSONB row is a free-form bag. Three writers exist:
//   - this POS build (canonical full-English shape),
//   - the waiter app (also canonical English), and
//   - an older wobpos build (short DB-column keys: `matnr`, `gamt`,
//     `categ`, `pouryn`, `pprice`, …).
//
// Normalize at the readTableOrderStore boundary: take whatever shape comes off
// the wire, pick the right value out for each canonical field, then
// validate via `TableOrderItemSchema`. zod's `.parse()` strips unknown
// keys for free, so we don't accumulate legacy hitchhikers.
// Idempotent — an already-canonical item passes through unchanged.

function num(value: unknown, fallback = 0): number {
  if (value == null) return fallback;
  const n = typeof value === "string" ? parseFloat(value) : (value as number);
  return typeof n === "number" && !isNaN(n) ? n : fallback;
}

function str(value: unknown, fallback = ""): string {
  return value == null ? fallback : String(value);
}

function bool(value: unknown): boolean {
  return value === true || value === "true" || value === "T";
}

export function adaptTableOrderItem(raw: unknown): TableOrderItem {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;
  // First defined value among the candidate keys (English first, then
  // the legacy spelling).
  const pick = (...keys: string[]): unknown => {
    for (const k of keys) {
      if (r[k] !== undefined && r[k] !== null) return r[k];
    }
    return undefined;
  };

  const candidate = {
    sno: num(pick("sno")),
    materialNumber: str(pick("materialNumber", "matnr")),
    material: str(pick("material", "matdesc", "description")),
    description: str(pick("description", "matdesc", "material")),
    qty: num(pick("qty", "quantity")),
    quantity: num(pick("quantity", "qty")),
    mrp: num(pick("mrp")),
    pourMrp: num(pick("pourMrp", "pourmrp")),
    value: num(pick("value")),
    stock: num(pick("stock")),
    currentStock: num(pick("currentStock", "curstock")),
    currentMl: num(pick("currentMl", "curml")),
    invoiceMl: num(pick("invoiceMl", "invml")),
    pourSize: num(pick("pourSize", "pourSizeMl", "poursize")),
    prepared: bool(pick("prepared")),
    preparedCount: num(pick("preparedCount", "prepared_count")),
    pourEnabled: str(pick("pourEnabled", "pouryn"), "N") === "Y" ? "Y" : "N",
    pourType: str(pick("pourType", "pourtype")),
    pourMaterialNumber: str(pick("pourMaterialNumber", "pmatnr")),
    pourDescription: str(pick("pourDescription", "pourdesc", "pmatdesc")),
    category: str(pick("category", "categ")),
    subCategory: str(pick("subCategory", "scateg")),
    materialType: str(pick("materialType", "mtype")),
    brandName: str(pick("brandName", "bname")),
    brandItem: str(pick("brandItem", "bitem")),
    hsnCode: str(pick("hsnCode", "hsncode")),
    cess: num(pick("cess")),
    additionalCess: num(pick("additionalCess", "addcess")),
    purchasePrice: num(pick("purchasePrice", "pprice")),
    weightedAveragePrice: num(pick("weightedAveragePrice", "wprice")),
    packageSize: str(pick("packageSize", "pksize")),
    packageType: str(pick("packageType", "pktype")),
    taxRate: num(pick("taxRate", "taxrate")),
    aroed: num(pick("aroed")),
    grossAmount: num(pick("grossAmount", "gamt")),
    discountAmount: num(pick("discountAmount", "discamt")),
    taxAmount: num(pick("taxAmount", "taxamt")),
    cessAmount: num(pick("cessAmount", "cessamt")),
    additionalCessAmount: num(pick("additionalCessAmount", "acessamt")),
    netAmount: num(pick("netAmount", "namt")),
    extraDiscount: num(pick("extraDiscount", "exdiscount")),
    discountFlag: str(pick("discountFlag", "eldiscyn"), "N"),
    discountType: str(pick("discountType", "disctype")),
    discountQuantity: num(pick("discountQuantity", "discqty")),
    discountPercent: num(pick("discountPercent", "discper")),
    offer: str(pick("offer")),
    offerType: num(pick("offerType", "offtype")),
    offerCategory: str(pick("offerCategory", "offcateg")),
    rowid: pick("rowid") !== undefined ? num(pick("rowid")) : undefined,
  };
  return TableOrderItemSchema.parse(candidate);
}

export function adaptTableOrderBillData(raw: unknown): TableOrderBillData {
  const bill = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;
  const rawOrders =
    bill.orders && typeof bill.orders === "object"
      ? (bill.orders as Record<string, unknown>)
      : {};
  const orders: Record<string, TableOrderItem> = {};
  for (const [key, item] of Object.entries(rawOrders)) {
    orders[key] = adaptTableOrderItem(item);
  }
  return { ...(bill as unknown as TableOrderBillData), orders };
}

// A `TableOrderBillData` is recognisable by carrying an `orders` map.
function isBillData(value: unknown): boolean {
  return (
    !!value &&
    typeof value === "object" &&
    "orders" in value &&
    typeof (value as { orders: unknown }).orders === "object"
  );
}

// readTableOrderStore's `data` can be a single bill, all bills for a table
// (`Record<bill, BillData>`), or all tables (`Record<table, Record<bill,
// BillData>>`), or `{}`. Walk it and normalize every bill found.
export function adaptReadTableOrderStoreData<T>(data: T): T {
  if (!data || typeof data !== "object") return data;
  if (isBillData(data)) return adaptTableOrderBillData(data) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    out[key] = adaptReadTableOrderStoreData(value);
  }
  return out as unknown as T;
}
